import React from "react";
import { Box, Typography, Stack, Pagination } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";

const ManifestGrid = ({
  columns,
  data,
  onRowClick,
  actions,
  loading,
  emptyMessage = "No data available.",
  pagination,
  compact = false,
}) => {
  const gridCols = columns.map(c => c.width || "1fr").join(" ");

  return (
    <Box sx={{
      width: "100%",
      overflowX: "auto",
      border: "1px solid color-mix(in srgb, var(--text-primary) 8%, transparent)",
      borderRadius: "12px",
      background: "rgba(10, 10, 10, 0.5)",
      "&::-webkit-scrollbar": { height: 4 },
      "&::-webkit-scrollbar-thumb": { background: "color-mix(in srgb, var(--text-primary) 15%, transparent)", borderRadius: 2 },
    }}>
     <Box sx={{ minWidth: columns.length > 4 ? 700 : "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          gap: 2,
          px: 3,
          py: 1.5,
          borderBottom: "1px solid color-mix(in srgb, var(--text-primary) 8%, transparent)",
          background: "color-mix(in srgb, var(--text-primary) 3%, transparent)",
          borderRadius: "12px 12px 0 0",
        }}
      >
        {columns.map((col, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              color: "color-mix(in srgb, var(--text-primary) 35%, transparent)",
              textTransform: "uppercase",
              fontFamily: '"Inter", sans-serif',
              letterSpacing: 1.2,
              fontWeight: 600,
              fontSize: "0.68rem"
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>

      <Box>
        <AnimatePresence>
          {loading ? (
             [...Array(5)].map((_, i) => (
                <Box key={i} sx={{
                  height: compact ? 48 : 56,
                  mx: 1,
                  my: 0.5,
                  background: "color-mix(in srgb, var(--text-primary) 3%, transparent)",
                  animation: "pulse 1.5s infinite",
                }} />
             ))
          ) : !data || data.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "color-mix(in srgb, var(--text-primary) 30%, transparent)", fontSize: "0.8rem" }}>
                {emptyMessage}
              </Typography>
            </Box>
          ) : (
            data.map((row, rowIndex) => (
              <motion.div
                key={row._id || rowIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: Math.min(rowIndex * 0.03, 0.3) }}
              >
                <Box
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridCols,
                    gap: 2,
                    px: 3,
                    py: compact ? 1.5 : 2,
                    borderBottom: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
                    background: rowIndex % 2 === 0
                      ? "transparent"
                      : "color-mix(in srgb, var(--text-primary) 2%, transparent)",
                    cursor: onRowClick ? "pointer" : "default",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      background: "color-mix(in srgb, var(--text-primary) 6%, transparent)",
                    }
                  }}
                >
                  {columns.map((col, colIndex) => (
                    <Box key={colIndex} sx={{ display: "flex", alignItems: "center", overflow: "hidden", minWidth: 0 }}>
                       {col.render ? col.render(row) : (
                         <Typography variant="body2" sx={{ color: "color-mix(in srgb, var(--text-primary) 80%, transparent)", fontFamily: '"Inter", sans-serif', fontSize: "0.85rem" }}>
                            {row[col.key]}
                         </Typography>
                       )}
                    </Box>
                  ))}
                </Box>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </Box>
     </Box>

      {pagination && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, px: 3, borderTop: "1px solid color-mix(in srgb, var(--text-primary) 8%, transparent)", borderRadius: "0 0 12px 12px" }}>
            <Pagination
                count={pagination.totalPages}
                page={pagination.current}
                onChange={pagination.onChange}
                size="small"
                sx={{
                    "& .MuiPaginationItem-root": {
                        color: "color-mix(in srgb, var(--text-primary) 50%, transparent)",
                        borderRadius: "4px",
                        fontSize: "0.75rem",
                        "&.Mui-selected": {
                             background: "color-mix(in srgb, var(--text-primary) 15%, transparent)",
                             color: "var(--text-primary)"
                        }
                    }
                }}
            />
        </Box>
      )}
    </Box>
  );
};

ManifestGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    key: PropTypes.string, // key in data object
    width: PropTypes.string, // css grid width (e.g. "2fr", "100px")
    render: PropTypes.func // (row) => ReactNode
  })).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  pagination: PropTypes.object
};

export default ManifestGrid;
